import { isPlatformBrowser } from '@angular/common';
import {
  Component,
  OnInit,
  ViewChild,
  ChangeDetectorRef,
  Inject,
  PLATFORM_ID,
} from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
  AsyncValidatorFn,
} from '@angular/forms';

import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { DropdownModule } from 'primeng/dropdown';
import { CalendarModule, Calendar } from 'primeng/calendar';
import { FloatLabelModule } from 'primeng/floatlabel';
import { CheckboxModule } from 'primeng/checkbox';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { QuillModule } from 'ngx-quill';
import { switchMap, map, of } from 'rxjs';

import { UserService } from '../../../services/user.service';
import { InventoryService } from '../../../services/inventory.service';
import { CourseService } from '../../../services/course.service';
import { NotificationService } from '../../../services/notification.service';
import {
  CategoryKind,
  parseCategoryKind,
} from '../../../utils/inventory-category-kind';

// Die Felder, die je nach Kategorie-Art zu- und abgeschaltet werden.
const DYNAMIC_FIELDS = [
  'model',
  'serialNumber',
  'assetTag',
  'puk2',
  'adminBenutzername',
  'adminPasswort',
  'passwort',
  'buchVersandStatus',
];

@Component({
  selector: 'app-inventory-dialog',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    DialogModule,
    ButtonModule,
    InputTextModule,
    DropdownModule,
    CalendarModule,
    FloatLabelModule,
    CheckboxModule,
    QuillModule,
  ],
  templateUrl: './inventory-dialog.component.html',
  styleUrl: './inventory-dialog.component.scss',
})
export class InventoryDialogComponent implements OnInit {
  private editItem: any | null = null;
  private isPatchingEdit = false;
  private editingInventoryId: number | null = null;

  mode: 'hardware' | 'category' = 'hardware';

  hardwareForm!: FormGroup;
  categoryForm!: FormGroup;

  hardwareCategories: any[] = [];
  participants: any[] = [];
  courses: any[] = [];
  statusOptions: any[] = [];
  books: any[] = [];

  // Muss beim ersten Rendern des Editors bereits stehen: ngx-quill reicht ein
  // leeres formats-Array unveraendert an Quill weiter (nur undefined faellt auf
  // die Standardformate zurueck), und die Toolbar verwirft dann jeden Button mit
  // "ignoring attaching to nonexistent format". Quill selbst wird von ngx-quill
  // nachgeladen, ein eigener Import ist nicht noetig.
  readonly quillModules = {
    toolbar: [
      ['bold', 'italic', 'underline', 'strike'],
      [{ header: 1 }, { header: 2 }],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ script: 'sub' }, { script: 'super' }],
      [{ indent: '-1' }, { indent: '+1' }],
      [{ direction: 'rtl' }],
      [{ size: [] }],
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      [{ color: [] }, { background: [] }],
      [{ font: [] }],
      [{ align: [] }],
      ['link', 'image'],
      ['clean'],
    ],
  };

  // Nur echte Quill-Formate: 'bullet' ist ein Wert des list-Formats und 'clean'
  // eine Toolbar-Aktion - beide gehoeren hier nicht hinein.
  readonly quillFormats = [
    'bold',
    'italic',
    'underline',
    'strike',
    'header',
    'list',
    'script',
    'indent',
    'direction',
    'size',
    'color',
    'background',
    'font',
    'align',
    'link',
    'image',
    'video',
    'code-block',
  ];

  isBrowser: boolean;

  selectedKind: CategoryKind | null = null;

  @ViewChild('startCal') startCal!: Calendar;
  @ViewChild('endCal') endCal!: Calendar;
  private suppressNextEndOpen = false;
  private suppressNextStartOpen = false;

  constructor(
    private fb: FormBuilder,
    private config: DynamicDialogConfig,
    private ref: DynamicDialogRef,
    private userService: UserService,
    private inventoryService: InventoryService,
    private cdr: ChangeDetectorRef,
    private courseService: CourseService,
    private notification: NotificationService,
    @Inject(PLATFORM_ID) platformId: Object,
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  ngOnInit(): void {
    this.initForms();
    this.wireRules();

    this.mode = this.config.data?.mode ?? 'hardware';

    this.inventoryService.getInventoryStatuses().subscribe({
      next: (list) => {
        this.statusOptions = list.map((s) => ({
          label: s.name,
          value: s.code,
        }));
      },
      error: (err) => {
        this.notification.error('Die Statusliste konnte nicht geladen werden.', err);
        this.statusOptions = [];
      },
    });

    this.participants = this.config.data?.participants ?? [];
    this.courses = this.config.data?.courses ?? [];

    this.loadCategoriesFromDb();
    this.loadBooks();

    if (this.mode === 'hardware' && this.participants.length === 0) {
      this.loadParticipantsFromUserService();
    }

    this.editItem = this.config.data?.inventory ?? null;

    this.loadCourses();
  }

  // =====================================================
  // LADEN
  // =====================================================

  private loadCourses(): void {
    this.courseService.getCourseObservable().subscribe({
      next: (courses) => {
        this.courses = [
          { courseId: 'none', courseName: '— Keine Auswahl —' },
          ...(courses ?? []),
        ];
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.notification.error('Die Kursliste konnte nicht geladen werden.', err);
        this.courses = [];
      },
    });
  }

  private loadParticipantsFromUserService(): void {
    this.userService.getUsersObservable().subscribe({
      next: (users) => {
        this.participants = [
          { id: 'none', fullName: '— Keine Auswahl —' },
          ...(users ?? []).map((u: any) => ({
            id: u.userId ?? u.id,
            fullName:
              u.fullName ?? `${u.firstname ?? ''} ${u.lastname ?? ''}`.trim(),
          })),
        ];
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.notification.error(
          'Die Teilnehmerliste konnte nicht geladen werden.',
          err,
        );
        this.participants = [];
        this.cdr.detectChanges();
      },
    });
  }

  private loadCategoriesFromDb(): void {
    this.inventoryService.getCategories().subscribe({
      next: (categories) => {
        this.hardwareCategories = (categories ?? []).map((c: any) => ({
          id: c.id ?? c.categoryId,
          name: c.name,
          kind: parseCategoryKind(c.kind),
        }));

        if (this.editItem) {
          this.patchFormForEdit(this.editItem);
        }

        this.cdr.detectChanges();
      },
      error: (err) => {
        this.notification.error(
          'Die Kategorieliste konnte nicht geladen werden.',
          err,
        );
        this.hardwareCategories = [];
        this.cdr.detectChanges();
      },
    });
  }

  // Ohne book_id-Spalte wird der gewaehlte Titel im model-Feld abgelegt; das
  // Dropdown verhindert lediglich Tippfehler.
  private loadBooks(): void {
    this.inventoryService.getBooks().subscribe({
      next: (books) => {
        this.books = books ?? [];
        this.cdr.detectChanges();
      },
      error: () => {
        this.books = [];
      },
    });
  }

  // =====================================================
  // FORMULARE
  // =====================================================

  private initForms(): void {
    this.hardwareForm = this.fb.group({
      courseId: ['none'],
      participant: ['none'],
      hardwareCategory: [null, Validators.required],

      startDate: [null],
      endDate: [null],
      returned: [false],

      model: [{ value: '', disabled: true }],
      serialNumber: [
        { value: '', disabled: true },
        { validators: [], asyncValidators: [], updateOn: 'blur' },
      ],

      assetTag: [{ value: '', disabled: true }],

      puk2: [{ value: '', disabled: true }],
      passwort: [{ value: '', disabled: true }],
      adminBenutzername: [{ value: '', disabled: true }],
      adminPasswort: [{ value: '', disabled: true }],

      buchVersandStatus: [{ value: 'Nicht versendet', disabled: true }],

      performanceItems: this.fb.array([]),
      note: [''],

      cpu: [''],
      hersteller: [''],
      ram: [''],
      speicher: [''],
      betriebssystem: [''],

      department: [''],
      location: [''],

      status: [''],
    });

    this.categoryForm = this.fb.group({
      name: ['', Validators.required],
    });
  }

  private wireRules(): void {
    this.hardwareForm.get('hardwareCategory')!.valueChanges.subscribe((id) => {
      this.applyCategoryRules(id);
    });
  }

  private applyCategoryRules(categoryId: any): void {
    const category = this.hardwareCategories.find(
      (c: any) => String(c.id) === String(categoryId),
    );

    this.selectedKind = category ? parseCategoryKind(category.kind) : null;

    if (!this.isPatchingEdit) {
      this.resetDynamicFields();
    }

    if (this.selectedKind === 'TECH') {
      this.enableTechFields();
    } else if (this.selectedKind === 'BOOK') {
      this.enableBookFields();
    }
  }

  private resetDynamicFields(): void {
    DYNAMIC_FIELDS.forEach((f) => {
      const c = this.hardwareForm.get(f)!;
      c.reset('', { emitEvent: false });
      c.disable({ emitEvent: false });
      c.clearValidators();
      c.clearAsyncValidators();
      c.updateValueAndValidity({ emitEvent: false });
    });

    this.clearPerformanceRows();
  }

  private enableTechFields(): void {
    ['model', 'serialNumber', 'assetTag', 'puk2', 'adminBenutzername', 'adminPasswort', 'passwort'].forEach(
      (f) => this.hardwareForm.get(f)!.enable({ emitEvent: false }),
    );

    const model = this.hardwareForm.get('model')!;
    const serial = this.hardwareForm.get('serialNumber')!;

    model.setValidators([Validators.required]);
    model.updateValueAndValidity({ emitEvent: false });

    serial.setValidators([Validators.required]);
    serial.setAsyncValidators([this.serialExistsValidator()]);
    serial.updateValueAndValidity();

    if (this.performanceItems.length === 0) {
      this.seedDefaultPerformanceRows();
    }
  }

  private enableBookFields(): void {
    const model = this.hardwareForm.get('model')!;
    const versand = this.hardwareForm.get('buchVersandStatus')!;

    model.enable({ emitEvent: false });

    versand.enable({ emitEvent: false });
    if (!versand.value) {
      versand.setValue('Nicht versendet', { emitEvent: false });
    }
  }

  private patchFormForEdit(item: any): void {
    this.editingInventoryId = item.inventoryId ?? item.id ?? null;

    this.isPatchingEdit = true;

    // Erst die Art bestimmen, damit die dynamischen Felder aktiv sind, bevor
    // patchValue sie befuellt - deaktivierte Controls ignorieren patchValue.
    this.resetDynamicFields();
    this.applyCategoryRules(item.categoryId ?? null);

    this.hardwareForm.patchValue(
      {
        courseId: item.courseId ?? 'none',
        participant: item.participantId ?? 'none',

        hardwareCategory: item.categoryId ?? null,

        startDate: item.startDate ? new Date(item.startDate) : null,
        endDate: item.endDate ? new Date(item.endDate) : null,

        returned: item.returned ?? false,

        note: item.note ?? '',

        assetTag: item.assetTag ?? '',

        puk2: item.puk2 ?? '',
        passwort: item.passwort ?? '',
        adminBenutzername: item.adminBenutzername ?? '',
        adminPasswort: item.adminPasswort ?? '',

        model: item.model ?? '',
        serialNumber: item.serialNumber ?? '',

        buchVersandStatus: item.buchVersandStatus ?? 'Nicht versendet',

        department: item.department ?? '',
        location: item.location ?? '',

        status: item.status ?? '',

        cpu: item.cpu ?? '',
        hersteller: item.hersteller ?? '',
        ram: item.ram ?? '',
        speicher: item.speicher ?? '',
        betriebssystem: item.betriebssystem ?? '',
      },
      { emitEvent: false },
    );

    if (this.selectedKind === 'TECH') {
      this.clearPerformanceRows();

      [
        { key: 'Hersteller', value: item.hersteller ?? '' },
        { key: 'CPU', value: item.cpu ?? '' },
        { key: 'RAM', value: item.ram ?? '' },
        { key: 'Speicher', value: item.speicher ?? '' },
        { key: 'Betriebssystem', value: item.betriebssystem ?? '' },
      ].forEach((p) =>
        this.performanceItems.push(this.createPerformanceRow(p.key, p.value)),
      );
    }

    this.isPatchingEdit = false;

    this.cdr.detectChanges();
  }

  // =====================================================
  // LEISTUNGSDATEN
  // =====================================================

  get performanceItems(): FormArray {
    return this.hardwareForm.get('performanceItems') as FormArray;
  }

  private createPerformanceRow(key = '', value = ''): FormGroup {
    return this.fb.group({
      key: [key],
      value: [value],
    });
  }

  addPerformanceRow(): void {
    this.performanceItems.push(this.createPerformanceRow());
  }

  removePerformanceRow(index: number): void {
    this.performanceItems.removeAt(index);
  }

  private seedDefaultPerformanceRows(): void {
    ['Hersteller', 'CPU', 'RAM', 'Speicher', 'Betriebssystem'].forEach((k) =>
      this.performanceItems.push(this.createPerformanceRow(k, '')),
    );
  }

  private clearPerformanceRows(): void {
    while (this.performanceItems.length > 0) {
      this.performanceItems.removeAt(0);
    }
  }

  // =====================================================
  // KALENDER
  // =====================================================

  onStartCalendarShow(): void {
    if (this.suppressNextStartOpen) {
      this.startCal?.hideOverlay();
      return;
    }
    this.endCal?.hideOverlay();
  }

  onEndCalendarShow(): void {
    if (this.suppressNextEndOpen) {
      this.endCal?.hideOverlay();
      return;
    }
    this.startCal?.hideOverlay();
  }

  onStartCalendarHide(): void {
    this.suppressNextEndOpen = true;
    setTimeout(() => (this.suppressNextEndOpen = false), 160);
  }

  onEndCalendarHide(): void {
    this.suppressNextStartOpen = true;
    setTimeout(() => (this.suppressNextStartOpen = false), 160);
  }

  // =====================================================
  // SPEICHERN
  // =====================================================

  submit(): void {
    if (this.mode === 'category') {
      this.categoryForm.markAllAsTouched();
      if (this.categoryForm.invalid) return;

      this.ref.close(this.categoryForm.value);
      return;
    }

    this.hardwareForm.markAllAsTouched();
    if (this.hardwareForm.invalid) return;

    const raw = this.hardwareForm.getRawValue();
    const isBook = this.selectedKind === 'BOOK';

    const perfMap = new Map<string, string>();

    (raw.performanceItems ?? []).forEach((p: any) => {
      const key = (p.key ?? '').toString().trim().toLowerCase();
      const value = (p.value ?? '').toString().trim();

      if (key) perfMap.set(key, value);
    });

    const payload = {
      categoryId: raw.hardwareCategory,

      participantId:
        raw.participant === 'none' || raw.participant == null
          ? null
          : raw.participant,

      courseId:
        raw.courseId === 'none' || raw.courseId == null ? null : raw.courseId,

      // Bei Buechern traegt model den Buchtitel.
      model: raw.model || null,
      serialNumber: isBook ? null : raw.serialNumber || null,

      cpu: isBook ? null : (perfMap.get('cpu') ?? null),
      hersteller: isBook ? null : (perfMap.get('hersteller') ?? null),
      ram: isBook ? null : (perfMap.get('ram') ?? null),
      speicher: isBook ? null : (perfMap.get('speicher') ?? null),
      betriebssystem: isBook ? null : (perfMap.get('betriebssystem') ?? null),

      // Nur Buecher fuehren einen Versandstatus.
      buchVersandStatus: isBook
        ? (raw.buchVersandStatus ?? 'Nicht versendet')
        : null,

      startDate: raw.startDate,
      endDate: raw.endDate,

      returned: !!raw.returned,

      note: raw.note,

      assetTag: isBook ? null : raw.assetTag || null,

      puk2: isBook ? null : raw.puk2 || null,
      passwort: isBook ? null : raw.passwort || null,
      adminBenutzername: isBook ? null : raw.adminBenutzername || null,
      adminPasswort: isBook ? null : raw.adminPasswort || null,

      department: raw.department,
      location: raw.location,

      status: raw.status,
    };

    this.ref.close(payload);
  }

  close(): void {
    this.ref.close();
  }

  private serialExistsValidator(): AsyncValidatorFn {
    return (control) => {
      const value = (control.value ?? '').toString().trim();
      if (!value) return of(null);

      return of(value).pipe(
        switchMap((v) =>
          this.inventoryService.checkSerialExists(
            v,
            this.editingInventoryId ?? undefined,
          ),
        ),
        map((exists) => (exists === true ? { serialExists: true } : null)),
      );
    };
  }

  onSerialBlur(): void {
    const ctrl = this.hardwareForm.get('serialNumber');
    if (!ctrl) return;

    ctrl.markAsTouched();
    ctrl.updateValueAndValidity({ emitEvent: true });
  }
}
